import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import MessageBox from "./MessageBox";
import api from "../services/api.service";
import * as Styles from "../styles/AddDiscount.Modal.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";
import { set } from "date-fns";

interface AddDiscountModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  orderTotal: any;
}

const AddDiscountModal: React.FC<AddDiscountModalProps> = ({
  isOpen,
  onRequestClose,
  orderTotal,
}) => {
  const {
    subsidiaryDataZustand,
    setOrderDataZustand,
    orderDataZustand,
    setMessageZustand,
  } = useCombinedStore((state) => {
    return {
      setSubsidiaryDataZustand: state.setSubsidiaryDataZustand,
      subsidiaryDataZustand: state.subsidiaryDataZustand,
      setOrderDataZustand: state.setOrderDataZustand,
      orderDataZustand: state.orderDataZustand,
      setMessageZustand: state.setMessageZustand,
    };
  });
  const products = orderDataZustand.products ?? [];
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedProducts =
    products.filter(
      (product: any) => product.productSelected
    ) || [];
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountType | null>({
    discountUuid: "00000000-0000-0000-0000-000000000000",
    discountName: "Sem Desconto",
    discountValue: "0",
  });

  interface DiscountType {
    discountUuid: string;
    discountName: string;
    discountValue: string;
  }

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value === "") {
      setSelectedDiscount({
        discountUuid: "00000000-0000-0000-0000-000000000000",
        discountName: "Sem Desconto",
        discountValue: "0",
      });
    } else {
      const selectDiscount =
        subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodDiscounts?.Discounts?.find(
          (discount: any) => discount.discountUuid === event.target.value
        );
      setSelectedDiscount(selectDiscount || null);
    }
  };
  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      await handleSubmit();
    }
  }

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (selectedDiscount && selectedProducts) {
      setIsSubmitting(true);

      // Lê a string do desconto (ex.: "10" ou "10%")
      let discountValueString = selectedDiscount.discountValue.trim();

      // Se NENHUM produto estiver selecionado => desconto na conta inteira
      if (selectedProducts.length === 0) {
        // DECISÃO DO DONO em 15/08/2026 (card 25 — ver
        // `_decisoes-lucco/desconto-da-conta-formato-e-base.md`): o formato
        // canônico gravado no banco é o PERCENTUAL.
        //
        // Antes daqui o web convertia "10%" em reais sobre o subtotal DAQUELE
        // INSTANTE e gravava "3.00". O desconto ficava congelado: item
        // lançado depois não recalculava. Agora o percentual vai CRU, como o
        // aplicativo já grava, e quem resolve é o leitor (`descontoDaConta`),
        // o cupom impresso e o fechamento de caixa — todos sobre a mesma base.
        let discountValueToSend = discountValueString;

        if (discountValueString.includes("%")) {
          // Para percentual o teto natural é 100%, não o total da conta.
          // `.replace(",", ".")` porque "10,5%" virava 10 — centavos perdidos.
          const numericValue =
            parseFloat(discountValueString.replace("%", "").replace(",", ".")) ||
            0;
          discountValueToSend =
            numericValue > 100 ? "100%" : `${numericValue}%`;
        } else {
          const discountValueNum =
            parseFloat(discountValueString.replace(",", ".")) || 0;

          // Sem o total da conta lido não dá para saber se o desconto cabe.
          // Antes, com o total ainda vazio (conta nova), o ajuste abaixo
          // gravava "0.00" EM SILÊNCIO e o desconto se perdia sem aviso.
          if (!orderTotal || orderTotal <= 0) {
            setMessageZustand(
              `Não foi possível ler o total da conta para aplicar o desconto. Abra a conta na busca e tente novamente.`,
              `error`
            );
            setIsSubmitting(false);
            return;
          }

          // Valor fixo continua limitado ao total da conta.
          if (discountValueNum > orderTotal) {
            discountValueToSend = orderTotal.toFixed(2);
            setSelectedDiscount((prev: any) => ({
              ...prev,
              discountValue: discountValueToSend,
            }));
          } else {
            discountValueToSend = discountValueNum.toFixed(2);
          }
        }

        try {
          await api.post(`addOrderDiscount/${orderDataZustand.orderUuid}`, {
            discount: {
              ...selectedDiscount,
              discountValue: discountValueToSend,
            },
          });
          setMessageZustand(`Desconto adicionado com sucesso!`, `success`);
        } catch (error: any) {
          if (error.message === "Network Error") {
            setMessageZustand(
              `Erro ao tentar adicionar o desconto, entre em contato com os administradores do Alfredo da sua unidade.`,
              `error`
            );
          } else {
            setMessageZustand(
              `Erro ao adicionar o desconto: ${error.message}`,
              `error`
            );
          }
        }
      } else {
        // Se produtos foram selecionados => desconto em cada produto
        for (let product of selectedProducts) {
          let discountValueNum = 0;

          // Converte product.productPrice para número
          const productPriceNum = parseFloat(product.productPrice);

          // Se productPriceNum não for um número válido, podemos decidir ignorar ou tratar o erro
          if (isNaN(productPriceNum)) {
            // Trate o caso, ex. pular, mostrar erro, etc.
            continue;
          }

          // Agora, discountValueNum pode ser calculado em relação a productPriceNum.
          // `.replace(",", ".")` porque "10,5%" virava 10 e "5,50" virava 5 —
          // centavos perdidos no desconto de produto (card 25).
          if (discountValueString.includes("%")) {
            const numericValue =
              parseFloat(
                discountValueString.replace("%", "").replace(",", ".")
              ) || 0;
            discountValueNum = (productPriceNum * numericValue) / 100;
          } else {
            discountValueNum =
              parseFloat(discountValueString.replace(",", ".")) || 0;
          }

          // Verifica se o desconto calculado não ultrapassa o valor do produto
          if (discountValueNum > productPriceNum) {
            discountValueNum = productPriceNum;
          }

          try {
            await api.post(`addOrderDiscount/${orderDataZustand.orderUuid}`, {
              discount: {
                ...selectedDiscount,
                discountValue: discountValueNum.toFixed(2),
              },
              productOrderUuid: product.productOrderUuid,
            });
            setMessageZustand(`Desconto adicionado com sucesso!`, `success`);
          } catch (error: any) {
            if (error.message === "Network Error") {
              setMessageZustand(
                `Erro ao tentar adicionar o desconto, entre em contato com os administradores do Alfredo da sua unidade.`,
                `error`
              );
            } else {
              setMessageZustand(
                `Erro ao adicionar o desconto: ${error.message}`,
                `error`
              );
            }
          }
        }
      }

      // Fecha modal, reseta desconto e libera o botão
      onRequestClose();
      setSelectedDiscount({
        discountUuid: "00000000-0000-0000-0000-000000000000",
        discountName: "Sem Desconto",
        discountValue: "0",
      });
      setIsSubmitting(false);
    } else {
      setMessageZustand(`Selecione um desconto e um produto disponíveis.`, `error`);
    }
  };





  useEffect(() => { }, [
    selectedProducts,
    setOrderDataZustand,
    orderDataZustand,
  ]);
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        onRequestClose();
        setSelectedDiscount({
          discountUuid: "00000000-0000-0000-0000-000000000000",
          discountName: "Sem Desconto",
          discountValue: "0",
        });
      }}
      style={Styles.customStyles}
      contentLabel="Main Modal"
      ariaHideApp={false}
    >
      <MessageBox />
      <Styles.ModalWrapper
        tabIndex={-1}
        ref={modalWrapperRef}
        onKeyDown={handleKeyDown}
      >
        <Styles.CloseButton onClick={() => {
          onRequestClose();
          setSelectedDiscount({
            discountUuid: "00000000-0000-0000-0000-000000000000",
            discountName: "Sem Desconto",
            discountValue: "0",
          });
        }}>
          &times;
        </Styles.CloseButton>
        <Styles.ModalContent>
          <Styles.ModalHeader
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
            <Styles.ModalTitle>Incluir Desconto</Styles.ModalTitle>
            <Styles.ModalDescription>
              Adicione um desconto para a conta.
            </Styles.ModalDescription>
          </Styles.ModalHeader>
          <Styles.ModalBody
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .backgroundColor
            }
          >
            <Styles.RowContainer>
              <Styles.TableContainer>
                <Styles.ProductsTable>
                  <tr>
                    <th>Código</th>
                    <th>Nome</th>
                    <th>
                      Incluir{" "}
                      <input
                        type="checkbox"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const newProducts = products.map((p: any) => {
                            return {
                              ...p,
                              productSelected: !p.productSelected,
                            };
                          });
                          setOrderDataZustand({
                            ...orderDataZustand,
                            products: newProducts,
                          });
                        }}
                        checked={true}
                      />
                    </th>
                  </tr>
                  {products.map((product: any, index: number) => (
                    <tr
                      key={product.productOrderUuid}
                      className={product.productSelected ? "selected" : ""}
                      onClick={() => {
                        const newProducts = products.map((p: any) => {
                          if (p.productOrderUuid === product.productOrderUuid) {
                            return {
                              ...p,
                              productSelected: !p.productSelected,
                            };
                          }
                          return p;
                        });
                        setOrderDataZustand({
                          ...orderDataZustand,
                          products: newProducts,
                        });
                      }}
                    >
                      <td>{product.productCode}</td>
                      <td>{product.productName}</td>
                      <td>
                        <input
                          type="checkbox"
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            e.stopPropagation(); // Impede a propagação para o <tr>
                            const newProducts = products.map((p: any) => {
                              if (p.productOrderUuid === product.productOrderUuid) {
                                return {
                                  ...p,
                                  productSelected: !p.productSelected,
                                };
                              }
                              return p;
                            });
                            setOrderDataZustand({
                              ...orderDataZustand,
                              products: newProducts,
                            });
                          }}
                          checked={product.productSelected}
                        />
                      </td>
                    </tr>

                  ))}
                </Styles.ProductsTable>
              </Styles.TableContainer>
            </Styles.RowContainer>
            <Styles.RowContainer>
              <Styles.FieldLabel>
                Selecione o desconto desejado:
              </Styles.FieldLabel>
              <Styles.DropdownInput
                color={
                  subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                    .buttonsColor
                }
              >
                <select onChange={handleChange}>
                  <option value="">Sem Desconto</option>
                  {subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodDiscounts?.Discounts?.map(
                    (discount: any, index: number) => (
                      <option key={index} value={discount.discountUuid}>
                        {discount.discountName}{" "}
                        {discount.discountValue.includes('%') ? discount.discountValue : Number(discount.discountValue).toFixed(2).replace(".", ",")}
                      </option>
                    )
                  )}
                </select>
                <label>Descontos</label>
              </Styles.DropdownInput>
            </Styles.RowContainer>
            <Styles.RowContainer>
              <Styles.FieldLabel>
                Ou adicione um desconto manual:
              </Styles.FieldLabel>
              <Styles.FieldCashContainer>
                <Styles.NumberCashInput
                  color={
                    subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                      .buttonsColor
                  }
                >
                  <input
                    type="text"
                    placeholder=""
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSelectedDiscount({
                        discountUuid: "00000000-0000-0000-0000-000000000000",
                        discountName: "Desconto Manual",
                        discountValue: String(e.target.value).replace(",", ".").replace("R$", ""),
                      });
                    }}
                  />
                  <label>Valor Manual</label>
                </Styles.NumberCashInput>
                <Styles.UnitCash
                  color={
                    subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                      .buttonsColor
                  }
                >
                  R$
                </Styles.UnitCash>
              </Styles.FieldCashContainer>
            </Styles.RowContainer>
          </Styles.ModalBody>
          <Styles.ModalFooter
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Button onClick={() => {
              onRequestClose();
              setSelectedDiscount({
                discountUuid: "00000000-0000-0000-0000-000000000000",
                discountName: "Sem Desconto",
                discountValue: "0",
              });
            }}>
              Cancelar
            </Styles.Button>
            <Styles.Button onClick={handleSubmit}>Adicionar</Styles.Button>
          </Styles.ModalFooter>
        </Styles.ModalContent>
      </Styles.ModalWrapper>
    </Modal>
  );
};

export default AddDiscountModal;
